/**
 * CheckInV2 config — ported (copied + slimmed) from pages/CheckIn per the V2
 * rule that V2 never imports V1 source. THE single staff roster: the legacy
 * folder had three competing hardcoded rosters; this one supersedes them.
 */

import type { CheckInStatus, StaffMember } from './types';

/** Priority order: T, TA, D, H — sticky columns on the calendar */
export const ACCOUNT_MANAGERS: StaffMember[] = [
  {
    id: 'tiffany',
    name: 'Tiffany',
    color: '#d998a3',
    email: 'tiffany@tifpaconsulting.com',
  },
  {
    id: 'tuanh',
    name: 'TuAnh',
    color: '#c5b845',
    email: 'tuanh@tifpaconsulting.com',
  },
  {
    id: 'dang',
    name: 'Dang',
    color: '#6366f1',
    email: 'dang@tifpaconsulting.com',
  },
  {
    id: 'huy',
    name: 'Huy',
    color: '#ec4899',
    email: 'huy@tifpaconsulting.com',
  },
];

export const TEAM_STAFF: StaffMember[] = [
  {
    id: 'amy',
    name: 'Amy',
    color: '#ef4444',
    email: 'amy@tifpaconsulting.com',
  },
  {
    id: 'lisa',
    name: 'Lisa',
    color: '#eab308',
    email: 'lisa@tifpaconsulting.com',
  },
  {
    id: 'lam',
    name: 'Lam',
    color: '#22c55e',
    email: 'lam@tifpaconsulting.com',
  },
  {
    id: 'ethan',
    name: 'Ethan',
    color: '#6b7280',
    email: 'ethan@tifpaconsulting.com',
  },
];

export const UNASSIGNED: StaffMember = {
  id: 'unassigned',
  name: 'Unassigned',
  color: '#94a3b8',
  email: null,
};

export const ALL_COLUMNS: StaffMember[] = [
  ...ACCOUNT_MANAGERS,
  UNASSIGNED,
  ...TEAM_STAFF,
];

/**
 * Everyone an appointment can be (re)assigned to — the same people the
 * calendar shows columns for. New appointments can only be booked into
 * manager slots, but reassignment covers the whole roster.
 */
export const ASSIGNABLE_STAFF: StaffMember[] = [
  ...ACCOUNT_MANAGERS,
  ...TEAM_STAFF,
];

export function staffByEmail(email: string | null | undefined): StaffMember {
  if (!email) return UNASSIGNED;
  return ALL_COLUMNS.find(s => s.email === email.toLowerCase()) ?? UNASSIGNED;
}

export function staffByName(name: string | null | undefined): StaffMember {
  if (!name) return UNASSIGNED;
  return (
    ALL_COLUMNS.find(s => s.name.toLowerCase() === name.toLowerCase()) ??
    UNASSIGNED
  );
}

/**
 * Google Calendar color_id → staff, for events created outside the portal.
 * 11=Tomato→Tiffany, 8=Graphite→TuAnh, 5=Banana→Dang, 2=Sage→Huy.
 */
export function staffByColorId(
  colorId: string | null | undefined
): StaffMember | null {
  const map: Record<string, string> = {
    '11': 'tiffany',
    '8': 'tuanh',
    '5': 'dang',
    '2': 'huy',
  };
  const id = colorId ? map[colorId] : undefined;
  return id ? ALL_COLUMNS.find(s => s.id === id)! : null;
}

export const STATUS_CONFIG: Record<
  CheckInStatus,
  {
    label: string;
    chip: string;
    block: string;
    /** Summary-card colors, as the Calendar's type cards use them */
    accent: { highlight: string; ink: string; tint: string };
  }
> = {
  waiting: {
    label: 'Waiting',
    chip: 'bg-amber-100 text-amber-800 border-amber-300',
    block: 'border-amber-400',
    accent: { highlight: '#fde68a', ink: '#92400e', tint: '#fffbeb' },
  },
  called: {
    label: 'Called',
    chip: 'bg-sky-100 text-sky-800 border-sky-300',
    block: 'border-sky-400',
    accent: { highlight: '#bae6fd', ink: '#075985', tint: '#f0f9ff' },
  },
  in_progress: {
    label: 'In Progress',
    chip: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    block: 'border-indigo-400',
    accent: { highlight: '#c7d2fe', ink: '#3730a3', tint: '#eef2ff' },
  },
  meeting: {
    label: 'Meeting',
    chip: 'bg-violet-100 text-violet-800 border-violet-300',
    block: 'border-violet-400',
    accent: { highlight: '#ddd6fe', ink: '#5b21b6', tint: '#f5f3ff' },
  },
  completed: {
    label: 'Completed',
    chip: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    block: 'border-emerald-400',
    accent: { highlight: '#bbf7d0', ink: '#166534', tint: '#f0fdf4' },
  },
  no_show: {
    label: 'No Show',
    chip: 'bg-rose-100 text-rose-800 border-rose-300',
    block: 'border-rose-400',
    accent: { highlight: '#fecdd3', ink: '#9f1239', tint: '#fff1f2' },
  },
  cancelled: {
    label: 'Cancelled',
    chip: 'bg-slate-100 text-slate-600 border-slate-300',
    block: 'border-slate-300',
    accent: { highlight: '#e2e8f0', ink: '#475569', tint: '#f8fafc' },
  },
};

/**
 * Forward transitions offered in the details panel per current status.
 * Flow: Check In → Meet → Complete (Cancel available throughout); 'called'
 * was dropped from the flow 2026-08-06 but legacy rows still render.
 */
export const NEXT_STATUSES: Record<CheckInStatus, CheckInStatus[]> = {
  waiting: ['meeting', 'completed', 'cancelled'],
  called: ['meeting', 'completed', 'cancelled'],
  in_progress: ['meeting', 'completed'],
  meeting: ['completed', 'cancelled'],
  completed: ['waiting'],
  no_show: ['waiting'],
  cancelled: ['waiting'],
};

export const STAGE_LABELS: Record<string, string> = {
  appointment_confirmation: 'Confirmed',
  client_organizer_data: 'Organizer (legacy)',
  check_in: 'Checked In',
  scan_documents: 'Scanning',
  data_entry: 'Data Entry',
  ready: 'Ready',
  call: 'Called',
  meeting: 'Meeting',
  end_meeting: 'Done',
};

/** Day-grid geometry */
export const GRID = {
  START_HOUR: 7,
  END_HOUR: 21, // exclusive: last row is 20:45
  SLOT_MINUTES: 15,
  SLOT_HEIGHT: 14, // px per 15-min row → 56px/hour
};

/** Scheduling slot window (office hours, from legacy TIME_SLOTS_CONFIG) */
export const SLOTS = {
  START_HOUR: 8,
  END_HOUR: 20,
  END_MINUTE: 45,
  INCREMENT_MINUTES: 15,
};

export const APPOINTMENT_DURATIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hr' },
  { value: 90, label: '1.5 hr' },
  { value: 120, label: '2 hr' },
] as const;

/**
 * Must match the check_in_links_appointment_type_check DB constraint
 * ('tax' | 'consulting' | '1099' | 'other') — anything else makes the
 * links/generate insert fail. (The legacy constants file this page first
 * ported from was stale and listed values the DB rejects.)
 */
export const APPOINTMENT_TYPES = [
  { value: 'tax', label: 'Tax' },
  { value: 'consulting', label: 'Consulting' },
  { value: '1099', label: '1099' },
  { value: 'other', label: 'Other' },
] as const;

export const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'vi', label: 'Tiếng Việt' },
] as const;

/** Calendar event types that never appear on the check-in board */
export const EXCLUDED_EVENT_TYPES = [
  'llc_renewal',
  'birthday',
  'office_closed',
] as const;
